// ─────────────────────────────────────────────────────────────────
// List block — inspector
// ----------------------------------------------------------------
// Three things are decided here: which list variable the block binds to, what
// the author can see about the item it repeats, and the rule between items.
//
// The item's *shape* is not edited here: the fields are the child variables of
// the list variable, which is where they are defined and defaulted (the
// variables panel). This panel reads them so the author can see what an item
// is made of, and shows the sample item an editor-time render uses.
// ─────────────────────────────────────────────────────────────────
import type { ListBlock } from '@blogforge/shared';
import type { DragPath } from '../../types';
import { useEditorStore } from '../../stores/editorStore';
import {
  listItemNames,
  listSampleItem,
  listVariableKeys,
  resolveListBinding,
  type ListVariableDefinition,
} from '../../lib/list-variables.js';

interface ListInspectorProps {
  block: ListBlock;
  path: DragPath;
  /** The document's stored `variableDefinition`, raw from the email row. */
  rawVariableDefinitions?: unknown;
}

const FALLBACK_DIVIDER_COLOR = '#E5E7EB';

export function ListInspector({ block, path, rawVariableDefinitions }: ListInspectorProps) {
  const updateBlock = useEditorStore((s) => s.updateBlock);
  const attrs = block.attrs;
  const binding = resolveListBinding(rawVariableDefinitions, attrs?.dataBindingKey ?? '');

  // Only a list variable can be bound: the block repeats *items*, and every
  // other type holds one value.
  const listKeys = listVariableKeys(rawVariableDefinitions);

  const dividerColor = attrs?.dividerColor ?? '';
  // A colour input only accepts `#rrggbb`, so an author-typed value it cannot
  // show falls back to the rule's own default rather than to black.
  const swatch = /^#[0-9a-fA-F]{6}$/.test(dividerColor) ? dividerColor : FALLBACK_DIVIDER_COLOR;

  const set = (patch: Partial<ListBlock['attrs']>) => {
    updateBlock(block.id, { attrs: { ...attrs, ...patch } }, path);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="field-label">List variable</label>
        <select
          className="field-input w-full text-xs"
          value={attrs?.dataBindingKey ?? ''}
          onChange={(e) => set({ dataBindingKey: e.target.value })}
        >
          <option value="">— not bound —</option>
          {listKeys.map((key) => (
            <option key={key} value={key}>
              {key}
            </option>
          ))}
        </select>

        {listKeys.length === 0 ? (
          // Nothing to bind to: the useful thing to say is where a list
          // variable comes from, not which key is stale.
          <p className="text-muted mt-1 text-xs">
            This document defines no list variable yet — add one in the variables panel, then bind
            it here.
          </p>
        ) : (
          !binding.ok && <p className="text-danger mt-1 text-xs">{binding.problem}</p>
        )}
      </div>

      {binding.ok && <ItemShape definition={binding.definition} />}

      <div className="space-y-2">
        <label className="field-label">Between items</label>
        <select
          className="field-input w-full text-xs"
          value={attrs?.dividerType ?? 'none'}
          onChange={(e) => set({ dividerType: e.target.value === 'line' ? 'line' : 'none' })}
        >
          <option value="none">No rule</option>
          <option value="line">A rule</option>
        </select>

        {attrs?.dividerType === 'line' && (
          <div className="flex items-center gap-3">
            <input
              type="color"
              className="border-line-strong bg-bg-soft h-9 w-9 shrink-0 cursor-pointer rounded-lg border p-1"
              value={swatch}
              onChange={(e) => set({ dividerColor: e.target.value })}
            />
            <input
              type="text"
              className="field-input font-mono text-xs"
              placeholder={FALLBACK_DIVIDER_COLOR}
              value={dividerColor}
              onChange={(e) => set({ dividerColor: e.target.value })}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * What an item is made of, and what it looks like with no values supplied: the
 * fields the list variable declares, with the sample item's value for each.
 * The sample item is the list's own `default[0]` — the same one a Preview
 * build and the canvas render with.
 */
function ItemShape({ definition }: { definition: ListVariableDefinition }) {
  const fields = listItemNames(definition);
  const sample = listSampleItem(definition);

  if (fields.length === 0) {
    return (
      <p className="text-muted text-xs">
        This list variable declares no fields yet — inside the item template, only the email&apos;s
        own variables are in scope. Add fields to it in the variables panel.
      </p>
    );
  }

  return (
    <div>
      <label className="field-label">Item fields</label>
      <ul className="border-line divide-line divide-y rounded-lg border">
        {fields.map((field) => (
          <li key={field} className="flex items-baseline gap-2 px-2 py-1">
            <code className="text-ink font-mono text-[11px]">{field}</code>
            <span className="text-muted min-w-0 flex-1 truncate text-right text-[11px]">
              {sample && sample[field] !== undefined ? String(sample[field]) : '—'}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-muted mt-1 text-[11px]">
        Inside the item template these names are in scope and shadow an email variable of the same
        name. Values shown are the sample item.
      </p>
    </div>
  );
}
